<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Motion } from 'motion-v';
import { useAuth } from '@/composables/useAuth';
import { useWindowScroll } from '@vueuse/core';
import { Amplitude } from '@seede/amplitude';

import { i18n, t } from '~/i18n';
import { localizedPath, normalizeSiteLocale, stripLocalePrefix } from '@/lib/site-locale';

const currentLocale = computed(() => normalizeSiteLocale(i18n.locale) ?? 'en');
const publicPath = (path: string) => localizedPath(currentLocale.value, path);

/** 默认 variant 的菜单 —— 首页 `/` 等 landing 页，label 走 landing_home.nav.* */
const menuItems = computed(() => [
  { id: 'features', to: publicPath('/#features'), label: t('landing_home.nav.features') },
  { id: 'pricing', to: publicPath('/pricing'), label: t('landing_home.nav.pricing') },
  { id: 'about', to: publicPath('/about'), label: t('landing_home.nav.about') },
  { id: 'spotlight', to: publicPath('/spotlight'), label: t('landing_home.nav.spotlight') },
]);

/**
 * v3 variant 的菜单 —— label 字段为「i18n key」而非已翻译字符串，
 * 模板里通过 $t(item.label) 渲染。Mobile 菜单的 overlay 也共用这份数据，
 * 所以只需要一处定义就能实现整个 v3 导航的多语言响应。
 */
const index3MenuItems = computed(() => [
  { id: 'use-case', to: publicPath('/solutions'), label: t('landing_v3.nav.use_case') },
  { id: 'pricing', to: publicPath('/pricing'), label: t('landing_v3.nav.pricing') },
]);

interface Props {
  activePath?: string;
  variant?: 'default' | 'index3';
}

const props = withDefaults(defineProps<Props>(), {
  activePath: '',
  variant: 'default',
});

const isCompactVariant = computed(() => props.variant === 'index3');

const overlayNavItems = computed(() =>
  isCompactVariant.value
    ? [
        ...index3MenuItems.value,
        { id: 'login', to: publicPath('/login'), label: t('landing_v3.nav.log_in') },
      ]
    : [...menuItems.value]
);

const { isAuthenticated } = useAuth();
const pagePath = computed(() => (import.meta.client ? window.location.href : ''));

const isLandingV3Page = computed(() => props.activePath === '/try');

const navAnalyticsProps = (extra: Record<string, unknown>) => {
  if (isLandingV3Page.value) {
    return {
      page_name: 'landing_v3',
      variant: 'v3',
      page_path: import.meta.client ? window.location.pathname : '',
      ...extra,
    };
  }
  return {
    page_path: pagePath.value,
    ...extra,
  };
};

const isActive = (path: string) => {
  return props.activePath === stripLocalePrefix(path);
};

const { y: scrollY } = useWindowScroll();
// 阶梯化：滚过 60px 后直接切到固定 alpha，避免每像素都触发 backdrop-filter 重绘；
// header 上配套的 transition-colors 会让这一次切换以 ~150ms 平滑完成
const SCROLL_BG_THRESHOLD = 60;
const SCROLL_BG_ALPHA = 0.3;
const bgOpacity = computed(() => (scrollY.value > SCROLL_BG_THRESHOLD ? SCROLL_BG_ALPHA : 0));

const mobileMenuOpen = ref(false);
const isClosing = ref(false);
const showMobileCtaButton = ref(false);
const isMobile = ref(false);

const openMobileMenu = () => {
  mobileMenuOpen.value = true;
  isClosing.value = false;
  document.body.style.overflow = 'hidden';
};

const closeMobileMenu = () => {
  isClosing.value = true;
  setTimeout(() => {
    mobileMenuOpen.value = false;
    isClosing.value = false;
    document.body.style.overflow = '';
  }, 350);
};

const toggleMobileMenu = () => {
  Amplitude.event(
    'cta_click',
    navAnalyticsProps({
      cta_name: mobileMenuOpen.value ? 'mobile_menu_close' : 'mobile_menu_open',
      section: 'top_nav',
    })
  );
  if (mobileMenuOpen.value) {
    closeMobileMenu();
  } else {
    openMobileMenu();
  }
};

const trackTopNavClick = (item: { id: string; label: string; to: string }) => {
  Amplitude.event(
    'nav_link_click',
    navAnalyticsProps({
      link_id: item.id,
      link_text: item.label,
      link_target: item.to,
      section: 'top_nav',
    })
  );
};

const handleMobileMenuNavClick = (item: { id: string; label: string; to: string }) => {
  trackTopNavClick(item);
  closeMobileMenu();
};

const trackLogoClick = (position: 'desktop' | 'mobile_menu') => {
  Amplitude.event(
    'nav_link_click',
    navAnalyticsProps({
      link_id: 'logo',
      link_text: 'Veeso AI',
      link_target: '/',
      section: position,
    })
  );
};

const trackActionLinkClick = (linkText: string, target: string, section: string) => {
  Amplitude.event(
    'nav_link_click',
    navAnalyticsProps({
      link_text: linkText,
      link_target: target,
      section,
      auth_state: isAuthenticated.value ? 'authenticated' : 'anonymous',
    })
  );
};

let observer: IntersectionObserver | null = null;

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);

  if (isCompactVariant.value) {
    showMobileCtaButton.value = true;
    return;
  }

  const target = document.getElementById('features-every-step');
  if (!target) return;

  observer = new IntersectionObserver(
    ([entry]) => {
      showMobileCtaButton.value = entry!.isIntersecting || entry!.boundingClientRect.top < 0;
    },
    { threshold: 0 }
  );
  observer.observe(target);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  observer?.disconnect();
});
</script>

<template>
  <header
    class="fixed top-0 left-0 right-0 w-full py-3 z-[1000] pointer-events-auto backdrop-blur-[10px] transition-colors duration-150"
    :style="{
      backgroundColor:
        mobileMenuOpen && !isClosing ? 'transparent' : `rgba(255, 255, 255, ${bgOpacity})`,
    }"
  >
    <div
      class="relative flex items-center justify-between gap-3 md:gap-6"
      :class="isCompactVariant ? 'w-full px-5' : 'px-4 md:px-6'"
    >
      <NuxtLink
        :to="publicPath('/')"
        class="inline-block shrink-0 transition-opacity duration-300 hover:opacity-70"
        @click="trackLogoClick('desktop')"
      >
        <img src="/veesoai-black.svg" :alt="t('landing_home.nav.logo_alt')" class="h-7 w-auto" />
      </NuxtLink>

      <nav v-if="!isCompactVariant" class="hidden md:flex gap-6 absolute left-1/2 -translate-x-1/2">
        <NuxtLink
          v-for="item in menuItems"
          :key="item.to"
          :to="item.to"
          @click="trackTopNavClick(item)"
          :class="[
            'text-sm font-medium transition-colors',
            isActive(item.to) ? 'text-black/90' : 'text-black/60 hover:text-black/90',
          ]"
        >
          {{ item.label }}
        </NuxtLink>
      </nav>

      <div
        v-else
        class="hidden md:flex flex-1 min-w-0 flex-wrap justify-end items-center gap-x-8 gap-y-2"
      >
        <NuxtLink
          v-for="item in index3MenuItems"
          :key="item.to"
          :to="item.to"
          @click="trackTopNavClick(item)"
          :class="[
            'text-sm font-medium transition-colors',
            isActive(item.to) ? 'text-black/90' : 'text-black/60 hover:text-black/90',
          ]"
        >
          {{ item.label }}
        </NuxtLink>
        <NuxtLink
          :to="publicPath('/login')"
          class="text-sm font-medium transition-colors text-black/60 hover:text-black/90"
          @click="trackActionLinkClick('Log in', '/login', 'top_nav_desktop')"
        >
          {{ $t('landing_v3.nav.log_in') }}
        </NuxtLink>
        <template v-if="!isAuthenticated">
          <NuxtLink
            :to="publicPath('/signup')"
            class="text-sm font-semibold bg-black/90 px-4 py-2 rounded-full transition-colors text-white hover:bg-black"
            @click="trackActionLinkClick('Start Free', '/signup', 'top_nav_desktop')"
          >
            {{ $t('landing_v3.nav.start_free') }}
          </NuxtLink>
        </template>
        <template v-else>
          <NuxtLink
            to="/workspace/create"
            class="text-sm font-medium px-4 py-2 rounded-full bg-black/90 text-white hover:bg-black transition-colors duration-300"
            @click="trackActionLinkClick('Workspace', '/workspace/create', 'top_nav_desktop')"
          >
            {{ $t('landing_v3.nav.workspace') }}
          </NuxtLink>
        </template>
      </div>

      <!-- index3/4 桌面端这个 wrapper 里只剩 md:hidden 的 mobile 元素，
         若不一起隐掉，外层 flex 的 md:gap-6 仍会和中间菜单产生 24px 间距，
         导致 Start Free 离右边距 比 logo 离左边距 多出 24px。 -->
      <div class="flex items-center gap-3 shrink-0" :class="isCompactVariant ? 'md:hidden' : ''">
        <template v-if="!isCompactVariant">
          <template v-if="!isAuthenticated">
            <NuxtLink
              :to="publicPath('/signup')"
              class="hidden md:block text-sm font-semibold bg-black/90 px-4 py-2 rounded-full transition-colors text-white hover:bg-black"
              @click="trackActionLinkClick('Start Free', '/signup', 'top_nav_desktop')"
            >
              {{ $t('landing_home.nav.start_free') }}
            </NuxtLink>
          </template>
          <template v-else>
            <NuxtLink
              to="/workspace/create"
              class="hidden md:block text-sm font-medium px-4 py-2 rounded-full bg-black/90 text-white hover:bg-black transition-colors duration-300"
              @click="trackActionLinkClick('Workspace', '/workspace/create', 'top_nav_desktop')"
            >
              {{ $t('landing_home.nav.workspace') }}
            </NuxtLink>
          </template>
        </template>

        <!-- Mobile: Show after scrolling -->
        <div class="md:hidden relative h-9 flex items-center">
          <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 scale-90"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-90"
          >
            <NuxtLink
              v-if="showMobileCtaButton && !isAuthenticated"
              :to="publicPath('/signup')"
              class="text-sm font-semibold bg-black/90 px-4 py-2 rounded-full transition-colors text-white hover:bg-black"
              @click="trackActionLinkClick('Start Free', '/signup', 'top_nav_mobile_sticky')"
            >
              {{
                $t(isCompactVariant ? 'landing_v3.nav.start_free' : 'landing_home.nav.start_free')
              }}
            </NuxtLink>
            <NuxtLink
              v-else-if="showMobileCtaButton && isAuthenticated"
              to="/workspace/create"
              class="text-sm font-medium px-4 py-2 rounded-full bg-black/90 text-white hover:bg-black transition-colors duration-300"
              @click="
                trackActionLinkClick('Workspace', '/workspace/create', 'top_nav_mobile_sticky')
              "
            >
              {{ $t(isCompactVariant ? 'landing_v3.nav.workspace' : 'landing_home.nav.workspace') }}
            </NuxtLink>
          </Transition>
        </div>

        <!-- Mobile Menu Button -->
        <button
          class="md:hidden flex flex-col justify-center items-center w-8 h-8 gap-1.5"
          @click="toggleMobileMenu"
          :aria-label="
            $t(
              isCompactVariant
                ? 'landing_v3.nav.toggle_menu_aria'
                : 'landing_home.nav.toggle_menu_aria'
            )
          "
        >
          <span
            class="block w-6 h-[1px] bg-black/90 transition-all duration-300"
            :class="{ 'rotate-45 translate-y-[4px]': mobileMenuOpen }"
          />
          <span
            class="block w-6 h-[1px] bg-black/90 transition-all duration-300"
            :class="{ '-rotate-45 -translate-y-[4px]': mobileMenuOpen }"
          />
        </button>
      </div>
    </div>
  </header>

  <!-- Mobile Menu Overlay -->
  <Teleport to="body">
    <Motion
      v-if="mobileMenuOpen"
      as="div"
      class="fixed inset-x-0 top-0 z-[999] bg-white/30 backdrop-blur-[60px] md:hidden overflow-hidden"
      :animate="isClosing ? { height: 0 } : { height: '100dvh' }"
      :initial="{ height: 0 }"
      :transition="{
        duration: isClosing ? 0.35 : 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: isClosing ? 0.15 : 0,
      }"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3">
        <NuxtLink
          :to="publicPath('/')"
          class="inline-block"
          @click="
            () => {
              trackLogoClick('mobile_menu');
              closeMobileMenu();
            }
          "
        >
          <img src="/veesoai-black.svg" :alt="t('landing_home.nav.logo_alt')" class="h-7 w-auto" />
        </NuxtLink>
        <button
          class="flex flex-col justify-center items-center w-8 h-8 gap-1.5"
          @click="
            () => {
              Amplitude.event(
                'cta_click',
                navAnalyticsProps({
                  cta_name: 'mobile_menu_close',
                  section: 'top_nav_mobile_menu',
                })
              );
              closeMobileMenu();
            }
          "
          :aria-label="
            $t(
              isCompactVariant
                ? 'landing_v3.nav.close_menu_aria'
                : 'landing_home.nav.close_menu_aria'
            )
          "
        >
          <span class="block w-6 h-[1px] bg-black/90 rotate-45 translate-y-[4px]" />
          <span class="block w-6 h-[1px] bg-black/90 -rotate-45 -translate-y-[4px]" />
        </button>
      </div>

      <!-- Navigation Links -->
      <nav class="flex flex-col items-center justify-center h-[calc(100dvh-120px)] gap-12">
        <Motion
          v-for="(item, i) in overlayNavItems"
          :key="item.to"
          as="div"
          :initial="{ opacity: 0, y: -20 }"
          :animate="isClosing ? { opacity: 0, y: -10 } : { opacity: 1, y: 0 }"
          :transition="
            isClosing
              ? { duration: 0.15, delay: 0.05 * (overlayNavItems.length - 1 - i) }
              : { duration: 0.6, delay: 0.25 + 0.08 * i, ease: [0.16, 1, 0.3, 1] }
          "
        >
          <NuxtLink
            :to="item.to"
            class="text-6xl font-light text-black/80 hover:text-black transition-colors"
            @click="handleMobileMenuNavClick(item)"
          >
            {{ item.label }}
          </NuxtLink>
        </Motion>
      </nav>

      <!-- CTA Button - Fixed Bottom -->
      <Motion
        as="div"
        class="absolute bottom-0 left-0 right-0 p-4"
        :initial="{ opacity: 0, y: 20 }"
        :animate="isClosing ? { opacity: 0, y: 10 } : { opacity: 1, y: 0 }"
        :transition="
          isClosing ? { duration: 0.12 } : { duration: 0.5, delay: 0.45, ease: [0.16, 1, 0.3, 1] }
        "
      >
        <template v-if="!isAuthenticated">
          <NuxtLink
            :to="publicPath('/signup')"
            class="block w-full text-center text-lg font-semibold bg-black/90 py-3 rounded-full text-white hover:bg-black transition-all"
            @click="
              () => {
                trackActionLinkClick('Start Free', '/signup', 'top_nav_mobile_menu');
                closeMobileMenu();
              }
            "
          >
            {{ $t(isCompactVariant ? 'landing_v3.nav.start_free' : 'landing_home.nav.start_free') }}
          </NuxtLink>
        </template>
        <template v-else>
          <NuxtLink
            to="/workspace/create"
            class="block w-full text-center text-lg font-medium py-3 rounded-full bg-black/90 text-white hover:bg-black transition-all"
            @click="
              () => {
                trackActionLinkClick('Workspace', '/workspace/create', 'top_nav_mobile_menu');
                closeMobileMenu();
              }
            "
          >
            {{ $t(isCompactVariant ? 'landing_v3.nav.workspace' : 'landing_home.nav.workspace') }}
          </NuxtLink>
        </template>
      </Motion>
    </Motion>
  </Teleport>
</template>
